import {
  createSocialPostEditableDraft,
  socialPostEditableDraftSchema,
  type SocialPostEditableDraft,
  type SocialPostScenePlanV1,
} from "@runcheetah/shared";

export { canPublishPostNow as canPublishStandalonePostNow } from "@/lib/post-publish-now";

export type StandalonePostCarouselSlideInput = {
  slide: {
    id: string;
    index: number;
  };
  scenePlan?: {
    plan?: SocialPostScenePlanV1 | null;
  } | null;
};

export function getStandalonePostBaselineDraft(input: {
  persistedDraft: SocialPostEditableDraft | null | undefined;
  copyDraft?: Pick<SocialPostEditableDraft, "caption" | "hashtags"> | null | undefined;
  baselinePlan: SocialPostScenePlanV1 | null | undefined;
  baselineCaption: string | null | undefined;
  baselineHashtags: string[] | null | undefined;
  carouselSlides: StandalonePostCarouselSlideInput[];
}): SocialPostEditableDraft | null {
  const baselineCarouselSlideDrafts = input.carouselSlides.flatMap((slideOutput) => {
    const plan = slideOutput.scenePlan?.plan ?? null;
    return plan
      ? [
          {
            slideId: slideOutput.slide.id,
            slideIndex: slideOutput.slide.index,
            plan,
            imageAssetOverrides: [],
          },
        ]
      : [];
  });

  if (input.persistedDraft) {
    if (baselineCarouselSlideDrafts.length === 0) {
      return input.persistedDraft;
    }

    const existingSlideDraftById = new Map(
      input.persistedDraft.carouselSlides.map((slideDraft) => [slideDraft.slideId, slideDraft]),
    );
    const carouselDraft = baselineCarouselSlideDrafts.map(
      (slideDraft) => existingSlideDraftById.get(slideDraft.slideId) ?? slideDraft,
    );

    return socialPostEditableDraftSchema.parse({
      ...input.persistedDraft,
      plan: carouselDraft[0]?.plan ?? input.persistedDraft.plan,
      carouselSlides: carouselDraft,
    });
  }

  if (!input.baselinePlan) {
    return null;
  }

  return createSocialPostEditableDraft({
    plan: input.baselinePlan,
    caption: input.copyDraft ? input.copyDraft.caption : input.baselineCaption,
    hashtags: input.copyDraft ? input.copyDraft.hashtags : (input.baselineHashtags ?? []),
    carouselSlides: baselineCarouselSlideDrafts,
  });
}

export function getStandalonePostDraftState(input: {
  currentDraftKey: string;
  baselineDraftKey: string;
  persistedDraftKey: string;
  publishedDraftKey: string;
  hasPersistedDraft: boolean;
  isPostEditable: boolean;
  isSavingDraft: boolean;
  isPublishingDraft: boolean;
}) {
  const hasUnsavedChanges =
    input.currentDraftKey !== input.baselineDraftKey ||
    input.persistedDraftKey !== input.publishedDraftKey;
  const hasUnpublishedDraft = input.persistedDraftKey !== input.publishedDraftKey;

  return {
    hasUnsavedChanges,
    hasUnpublishedDraft,
    canSaveChanges:
      input.isPostEditable && hasUnsavedChanges && !input.isSavingDraft && !input.isPublishingDraft,
  };
}
